import { useEffect, useState } from "react";
import { authFetch } from "./authFetch";
import { codeModuleMeta } from "../data/securityModules";
import { applyLayer, asLayer, pickParts, type ModuleLayer, type ModuleSnapshot } from "./moduleUpstream";

/**
 * Update-Hinweise für angepasste Standardmodule: hat SHERLOX AWARE (bzw. der
 * Code-Stand) seit dem Speichern etwas geändert, das eine Anpassung überdeckt?
 *
 * Der Browser baut die aktuelle Fassung darunter (den Code-Stand kennt nur er),
 * der Server bereinigt sie und vergleicht die Fingerabdrücke
 * (api/_moduleOverride.mjs → changedUpstreamParts). Im Kundenbereich über das
 * eigene Token (api/entitlements.js), im GA-Portal über die Session (api/ga.js).
 */

export const MODULE_UPDATES_EVENT = "sherlox:module-updates-changed";

export type CheckTarget = { kind: "self"; tenantId: string } | { kind: "ga"; tenantId?: string };

export type UpstreamCheck = {
  moduleId: string;
  /** Gibt es für die Anpassung einen gespeicherten Bezugspunkt? */
  tracked: boolean;
  /** Bereiche, in denen sich die Fassung darunter geändert hat. */
  changed: string[];
  /** Nur mit `detail`: aktuelle Fassung darunter (bereinigt). */
  now?: unknown;
  /** Nur mit `detail`: Bezugspunkt beim Speichern (bereinigt). */
  base?: unknown;
};

type TrackedRow = { module_id: string; upstream_fp?: unknown; updated_by?: string | null; updated_at?: string | null };

/** Was der Adminbereich meldet: offene Updates und kürzlich eingespielte Pflicht-Updates. */
export type ModuleNotices = { pending: string[]; mandatory: Array<{ moduleId: string; at: string }> };

const EMPTY_NOTICES: ModuleNotices = { pending: [], mandatory: [] };
const MANDATORY_AUTHOR = "sherlox-pflicht";
// Pflicht-Updates bleiben so lange in der Glocke, danach stehen sie nur noch im Verlauf.
const MANDATORY_NOTICE_MS = 30 * 24 * 60 * 60 * 1000;

function mandatoryNotices(rows: TrackedRow[], now = Date.now()): ModuleNotices["mandatory"] {
  return rows
    .filter(r => r.updated_by === MANDATORY_AUTHOR && typeof r.updated_at === "string" && now - Date.parse(r.updated_at) < MANDATORY_NOTICE_MS)
    .map(r => ({ moduleId: r.module_id, at: r.updated_at as string }));
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
export const hasFingerprints = (v: unknown) => isObj(v) && Object.keys(v).length > 0;

/** Auslieferungsstand eines Moduls aus dem Code (Metadaten + beide Sprachen). */
export async function codeSnapshot(moduleId: string): Promise<ModuleSnapshot | null> {
  const meta = codeModuleMeta(moduleId);
  if (!meta) return null;
  // Lazy: LearningModule zieht die komplette Modul-Datenbasis nach.
  const { codeModuleContent } = await import("../pages/LearningModule");
  const [de, en] = await Promise.all([codeModuleContent(moduleId, "de"), codeModuleContent(moduleId, "en")]);
  return {
    icon: meta.icon,
    color: meta.color,
    level: meta.level,
    forceVideo: false,
    meta: { de: meta.de, en: meta.en },
    content: { de: de ?? { blocks: [], questions: [] }, en: en ?? { blocks: [], questions: [] } },
  };
}

/** Code-Stand plus die Ebenen darüber (z. B. die globale Fassung). */
export async function upstreamSnapshot(moduleId: string, layers: Array<ModuleLayer | null>): Promise<ModuleSnapshot | null> {
  const code = await codeSnapshot(moduleId);
  if (!code) return null;
  return layers.reduce<ModuleSnapshot>((s, layer) => applyLayer(s, layer), code);
}

export async function checkUpstream(
  target: CheckTarget,
  modules: Array<{ moduleId: string; upstream: ModuleLayer }>,
  detail = false,
): Promise<UpstreamCheck[] | null> {
  if (modules.length === 0) return [];
  try {
    const res = target.kind === "self"
      ? await authFetch("/api/entitlements", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: target.tenantId, moduleOverride: { check: modules, detail } }),
        })
      : await fetch("/api/ga?action=check-upstream", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...(target.tenantId ? { tenantId: target.tenantId } : {}), modules, detail }),
        });
    if (!res.ok) return null;
    const data = await res.json() as { modules?: UpstreamCheck[] };
    return Array.isArray(data.modules) ? data.modules : [];
  } catch {
    return null;
  }
}

export async function acknowledgeUpstream(target: CheckTarget, moduleId: string, upstream: ModuleLayer): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = target.kind === "self"
      ? await authFetch("/api/entitlements", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: target.tenantId, moduleOverride: { moduleId, acknowledge: true, upstream } }),
        })
      : await fetch("/api/ga?action=acknowledge-upstream", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...(target.tenantId ? { tenantId: target.tenantId } : {}), moduleId, upstream }),
        });
    if (res.ok) return { ok: true };
    const data = await res.json().catch(() => ({})) as { error?: string; detail?: string };
    return { ok: false, error: data.error === "migration_missing" ? "Update-Hinweise sind noch nicht eingerichtet." : (data.detail ?? data.error ?? `HTTP ${res.status}`) };
  } catch (err) {
    return { ok: false, error: String(err instanceof Error ? err.message : err) };
  }
}

/**
 * Module mit neuer Fassung darunter. `globals` = die globale Ebene je Modul
 * (leer, wenn die Anpassung selbst die globale ist). Geschickt werden nur die
 * Bereiche, die die Anpassung überdeckt – der Abgleich bleibt dadurch klein.
 */
export async function pendingUpdates(
  target: CheckTarget,
  rows: TrackedRow[],
  globals: Record<string, unknown>,
): Promise<string[]> {
  const tracked = rows.filter(r => hasFingerprints(r.upstream_fp));
  if (tracked.length === 0) return [];
  const modules = (await Promise.all(tracked.map(async row => {
    const upstream = await upstreamSnapshot(row.module_id, [globals[row.module_id] ? asLayer(globals[row.module_id]) : null]);
    if (!upstream) return null;
    return { moduleId: row.module_id, upstream: pickParts(upstream, Object.keys(row.upstream_fp as object)) };
  }))).filter((m): m is { moduleId: string; upstream: ModuleLayer } => m !== null);
  const result = await checkUpstream(target, modules);
  return (result ?? []).filter(m => m.changed.length > 0).map(m => m.moduleId);
}

// Ein Abgleich je Sitzung und Mandant reicht: neue SHERLOX-Fassungen kommen mit
// einem Deploy (neues Bundle, neuer Seitenaufruf) oder über das GA-Portal.
// Speichern im Editor stößt über MODULE_UPDATES_EVENT einen neuen Abgleich an.
let cache: { tenantId: string; at: number; notices: ModuleNotices } | null = null;
const CACHE_MS = 10 * 60 * 1000;

/** Für den Adminbereich: welche angepassten Module haben eine neue SHERLOX-Fassung bzw. ein Pflicht-Update? */
export function useTenantModuleUpdates(tenantId: string | null | undefined, enabled: boolean): ModuleNotices {
  const [notices, setNotices] = useState<ModuleNotices>(() => (cache && cache.tenantId === tenantId ? cache.notices : EMPTY_NOTICES));
  useEffect(() => {
    if (!enabled || !tenantId) {
      setNotices(EMPTY_NOTICES);
      return;
    }
    let cancelled = false;
    const run = async (force: boolean) => {
      if (!force && cache && cache.tenantId === tenantId && Date.now() - cache.at < CACHE_MS) {
        setNotices(cache.notices);
        return;
      }
      try {
        const res = await authFetch(`/api/entitlements?tenantModules=1&withGlobals=1&tenantId=${encodeURIComponent(tenantId)}`);
        if (!res.ok) return;
        const data = await res.json() as { modules?: TrackedRow[]; globals?: Record<string, unknown> };
        const rows = data.modules ?? [];
        const pending = await pendingUpdates({ kind: "self", tenantId }, rows, data.globals ?? {});
        const next = { pending, mandatory: mandatoryNotices(rows) };
        cache = { tenantId, at: Date.now(), notices: next };
        if (!cancelled) setNotices(next);
      } catch {
        // Ohne Abgleich kein Hinweis – der Adminbereich läuft normal weiter.
      }
    };
    void run(false);
    const onChange = () => { void run(true); };
    window.addEventListener(MODULE_UPDATES_EVENT, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(MODULE_UPDATES_EVENT, onChange);
    };
  }, [tenantId, enabled]);
  return notices;
}
